import React, { useCallback, useEffect, useState } from "react";
import { TitleTag } from "@/components/shared/item";
import styled from "@emotion/styled";
import {
  CommonContainer,
  CommonPageContainer,
} from "@/components/ui/container";
import { CommonTitleFour, CommonTitleTwo } from "@/components/ui/fonts/Fonts";
import { color } from "@/theme";
import { useLocation, useNavigate } from "react-router-dom";
import { PrimaryButton } from "@/components/ui/buttons";
import { isCampaignActive, isCampaignEnd } from "@/utils/Functions";
import ShortFormCampaignDetail from "./ShortFormCampaignDetail";
import ShortFormCampaignFormModal from "@/components/ui/modal/short-form-campaign/ShortFormCampaignFormModal";
import { useUserStore } from "@/store/useUserStore";
import { Popconfirm } from "antd";
import { SHORT_FORM_PAST_WINNER_VIDEOS } from "@/constants/staticInformation";
import { mq } from "@/libs/react-responsive/mediaQuery";

const PageTitle = styled(CommonTitleTwo)(() => ({
  margin: "2rem 0",
  textAlign: "center",
}));

const ButtonWrapper = styled.div(() => ({
  width: "100%",
  marginTop: "8rem",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const PastWinnerVideos = styled.div(() => ({
  marginTop: "8rem",
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  textAlign: "center",
}));

const YoutubeItem = styled.iframe(() => ({
  width: "100%",
  aspectRatio: "5 / 3",
}));

const VideosWrapper = styled.div(() => ({
  display: "flex",
  flexDirection: "column",
  marginTop: "2rem",
}));

const VideoItem = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",

  [mq("tablet")]: {
    flexDirection: "row",

    "&:first-of-type": {
      header: {
        borderTop: `1px solid ${theme.color.grey04}`,
      },

      "& > div": {
        borderTop: `1px solid ${theme.color.grey04}`,
      },
    },
  },

  header: {
    width: "100%",
    textAlign: "left",
    fontSize: "1.6rem",
    letterSpacing: "0.2rem",
    padding: ".6rem 1rem",
    borderBottom: `1px dashed #F5F5F5`,
    borderTop: `1px solid ${theme.color.grey04}`,

    [mq("tablet")]: {
      width: "16rem",
      textAlign: "center",

      borderTop: "none",
      borderRight: `1px solid ${theme.color.grey04}`,
      borderBottom: `1px solid ${theme.color.grey04}`,
    },

    [mq("desktop")]: {
      width: "24rem",
      fontSize: "2.4rem",
    },
  },

  "& > div": {
    flex: 1,
    padding: "1rem 0 1rem 1rem",
    width: "100%",

    [mq("tablet")]: {
      borderRight: `1px solid ${theme.color.grey04}`,
      borderBottom: `1px solid ${theme.color.grey04}`,
    },
  },
}));

const YoutubeLinkWrapper = styled.a(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4rem",
  fontSize: "1.6rem",
  marginTop: "4rem",

  [mq("desktop")]: {
    fontSize: "2.4rem",
  },
}));

const ShortFormCampaign = ({ test }) => {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [isWithinRange, setIsWithinRange] = useState(false);
  const [open, setOpen] = useState(false);

  const { search } = useLocation();

  const params = new URLSearchParams(search);

  const apply = params.get("apply");

  const [modalOpen, setModalOpen] = useState(false);

  const onCancel = useCallback(() => {
    setModalOpen(false);

    // URL에 남아있는 ?apply=true 제거
    const nextParams = new URLSearchParams(search);
    nextParams.delete("apply");
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: "/board/65초-공모전",
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [navigate, search]);

  useEffect(() => {
    if (apply && isWithinRange) {
      setModalOpen(true);
    }
  }, [apply, isWithinRange]);

  useEffect(() => {
    const tick = () => {
      if (isCampaignEnd() && !isCampaignActive()) {
        setIsWithinRange(false);
        setModalOpen(false);
        return;
      }

      setIsWithinRange(test || isCampaignActive());
    };

    tick();
    const timer = setInterval(tick, 1000);

    return () => clearInterval(timer);
  }, [test]);

  const handleLogin = () => {
    navigate("/login?redirect=/board/65초-공모전?apply=true");
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenLoginModal = () => {
    if (!user) {
      setOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  return (
    <CommonPageContainer>
      <CommonContainer>
        {user && (
          <ShortFormCampaignFormModal
            modalOpen={modalOpen}
            onCancel={onCancel}
          />
        )}

        <TitleTag title="65초 공모전" bgColor={color.primary02} />

        <PageTitle>
          <p>65초 안에 지구를 바꿔줘!</p>

          <p>사단법인 세이브더얼스 제 2회 환경소재 숏폼 공모전</p>
        </PageTitle>

        <ShortFormCampaignDetail />

        {isWithinRange && (
          <Popconfirm
            okText="로그인"
            cancelText="취소"
            placement="top"
            title="65초 공모전 지원하기"
            description={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  whiteSpace: "nowrap",
                }}
              >
                <p>해당 공모전에 지원하기 위해서는 로그인이 필요합니다.</p>

                <p>로그인 후 지원하시겠습니까?</p>
              </div>
            }
            icon={false}
            open={open}
            onConfirm={handleLogin}
            onCancel={handleCancel}
            zIndex={5001}
            autoFocus={false}
          >
            <ButtonWrapper>
              <PrimaryButton
                bgColor={color.tertiary}
                clickEvent={handleOpenLoginModal}
              >
                지원 접수
              </PrimaryButton>
            </ButtonWrapper>
          </Popconfirm>
        )}

        {/* 2025년 공모전 수상작 */}
        {isWithinRange && (
          <PastWinnerVideos>
            <CommonTitleFour>2025년 공모전 수상작</CommonTitleFour>

            <VideosWrapper>
              {SHORT_FORM_PAST_WINNER_VIDEOS.map(video => {
                const regExp =
                  /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;

                const matches = video.link?.match(regExp);

                return (
                  <VideoItem key={video.id}>
                    <header>{video.title}</header>

                    <div>
                      <YoutubeItem
                        src={"https://www.youtube.com/embed/" + matches[7]}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; "
                        allowFullScreen
                      ></YoutubeItem>
                    </div>
                  </VideoItem>
                );
              })}
            </VideosWrapper>

            <YoutubeLinkWrapper
              href="https://www.youtube.com/playlist?list=PLPR1vBmMPS9as8i_9eNohWvavVlixosgg"
              target="_blank"
              rel="noreferrer"
            >
              <span>전체 수상작 보러가기</span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                id="yt-ringo2-svg_yt10"
                width="150"
                height="32"
                viewBox="0 0 93 20"
                focusable="false"
                aria-hidden="true"
              >
                <g>
                  <path
                    d="M14.4848 20C14.4848 20 23.5695 20 25.8229 19.4C27.0917 19.06 28.0459 18.08 28.3808 16.87C29 14.65 29 9.98 29 9.98C29 9.98 29 5.34 28.3808 3.14C28.0459 1.9 27.0917 0.94 25.8229 0.61C23.5695 0 14.4848 0 14.4848 0C14.4848 0 5.42037 0 3.17711 0.61C1.9286 0.94 0.954148 1.9 0.59888 3.14C0 5.34 0 9.98 0 9.98C0 9.98 0 14.65 0.59888 16.87C0.954148 18.08 1.9286 19.06 3.17711 19.4C5.42037 20 14.4848 20 14.4848 20Z"
                    fill="#FF0033"
                  ></path>
                  <path d="M19 10L11.5 5.75V14.25L19 10Z" fill="white"></path>
                </g>
                <g id="youtube-paths_yt10">
                  <path d="M37.1384 18.8999V13.4399L40.6084 2.09994H38.0184L36.6984 7.24994C36.3984 8.42994 36.1284 9.65994 35.9284 10.7999H35.7684C35.6584 9.79994 35.3384 8.48994 35.0184 7.22994L33.7384 2.09994H31.1484L34.5684 13.4399V18.8999H37.1384Z"></path>
                  <path d="M44.1003 6.29994C41.0703 6.29994 40.0303 8.04994 40.0303 11.8199V13.6099C40.0303 16.9899 40.6803 19.1099 44.0403 19.1099C47.3503 19.1099 48.0603 17.0899 48.0603 13.6099V11.8199C48.0603 8.44994 47.3803 6.29994 44.1003 6.29994ZM45.3903 14.7199C45.3903 16.3599 45.1003 17.3899 44.0503 17.3899C43.0203 17.3899 42.7303 16.3499 42.7303 14.7199V10.6799C42.7303 9.27994 42.9303 8.02994 44.0503 8.02994C45.2303 8.02994 45.3903 9.34994 45.3903 10.6799V14.7199Z"></path>
                  <path d="M52.2713 19.0899C53.7313 19.0899 54.6413 18.4799 55.3913 17.3799H55.5013L55.6113 18.8999H57.6012V6.53994H54.9613V16.4699C54.6812 16.9599 54.0312 17.3199 53.4212 17.3199C52.6512 17.3199 52.4113 16.7099 52.4113 15.6899V6.53994H49.7812V15.8099C49.7812 17.8199 50.3613 19.0899 52.2713 19.0899Z"></path>
                  <path d="M62.8261 18.8999V4.14994H65.8661V2.09994H57.1761V4.14994H60.2161V18.8999H62.8261Z"></path>
                  <path d="M67.8728 19.0899C69.3328 19.0899 70.2428 18.4799 70.9928 17.3799H71.1028L71.2128 18.8999H73.2028V6.53994H70.5628V16.4699C70.2828 16.9599 69.6328 17.3199 69.0228 17.3199C68.2528 17.3199 68.0128 16.7099 68.0128 15.6899V6.53994H65.3828V15.8099C65.3828 17.8199 65.9628 19.0899 67.8728 19.0899Z"></path>
                  <path d="M80.6744 6.26994C79.3944 6.26994 78.4744 6.82994 77.8644 7.73994H77.7344C77.8144 6.53994 77.8744 5.51994 77.8744 4.70994V1.43994H75.3244L75.3144 12.1799L75.3244 18.8999H77.5444L77.7344 17.6999H77.8044C78.3944 18.5099 79.3044 19.0199 80.5144 19.0199C82.5244 19.0199 83.3844 17.2899 83.3844 13.6099V11.6999C83.3844 8.25994 82.9944 6.26994 80.6744 6.26994ZM80.7644 13.6099C80.7644 15.9099 80.4244 17.2799 79.3544 17.2799C78.8544 17.2799 78.1644 17.0399 77.8544 16.5899V9.23994C78.1244 8.53994 78.7244 8.02994 79.3944 8.02994C80.4744 8.02994 80.7644 9.33994 80.7644 11.7299V13.6099Z"></path>
                  <path d="M92.6517 11.4999C92.6517 8.51994 92.3517 6.30994 88.9217 6.30994C85.6917 6.30994 84.9717 8.45994 84.9717 11.6199V13.7899C84.9717 16.8699 85.6317 19.1099 88.8417 19.1099C91.3817 19.1099 92.6917 17.8399 92.5417 15.3799L90.2917 15.2599C90.2617 16.7799 89.9117 17.3999 88.9017 17.3999C87.6317 17.3999 87.5717 16.1899 87.5717 14.3899V13.5499H92.6517V11.4999ZM88.8617 7.96994C90.0817 7.96994 90.1717 9.11994 90.1717 11.0699V12.0799H87.5717V11.0699C87.5717 9.13994 87.6517 7.96994 88.8617 7.96994Z"></path>
                </g>
              </svg>
            </YoutubeLinkWrapper>
          </PastWinnerVideos>
        )}
      </CommonContainer>
    </CommonPageContainer>
  );
};

export default ShortFormCampaign;
